import React from 'react'
import { supabase } from '@/lib/supabase'
import { applyOptimisticReactionToggle } from '@/shared/utils/reactions'
import {
  addAnalysisMessageAttachments,
  fetchAnalysisCommunityThread,
  postAnalysisCommunityMessage,
  toggleAnalysisCardReaction,
  toggleAnalysisMessageReaction,
  deleteAnalysisCommunityMessage,
  updateAnalysisCommunityMessage,
} from '../services/analysisCommunityService'
import type { CommunityThread } from '@/modules/comunidade/services/communityService'
import { uploadPublicFile } from '@/modules/upload/services/uploadService'
import { validateCommunityAttachmentFile } from '@/modules/comunidade/utils/communityAttachments'
import {
  appendEditedSuffixToHtml,
  prepareCommunityMessageContent,
  stripEditedSuffixFromHtml,
} from '@/modules/comunidade/utils/communityMessageContent'
import { emitCommunityThreadNotificationsAutoEnabled } from '@/modules/comunidade/services/communityThreadNotificationsEvents'

export function useAnalysisCommunityThread(
  analysisId: number | null,
  currentUser: { name: string; avatarUrl?: string | null } | null,
  active: boolean,
) {
  const [loading, setLoading] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const [loadedOnce, setLoadedOnce] = React.useState(false)
  const [thread, setThread] = React.useState<CommunityThread>({
    messages: [],
    cardReactions: [],
  })

  const load = React.useCallback(async () => {
    if (!analysisId || !active) {
      setThread({ messages: [], cardReactions: [] })
      setLoadedOnce(false)
      return
    }
    setLoading(true)
    try {
      const data = await fetchAnalysisCommunityThread(analysisId)
      setThread(data)
      setLoadedOnce(true)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useAnalysisCommunityThread load error', error)
    } finally {
      setLoading(false)
    }
  }, [active, analysisId])

  React.useEffect(() => {
    if (!analysisId || !active) {
      setThread({ messages: [], cardReactions: [] })
      setLoadedOnce(false)
      return
    }
    let alive = true
    ;(async () => {
      await load()
      if (!alive) return
    })()
    return () => {
      alive = false
    }
  }, [active, load, analysisId])

  React.useEffect(() => {
    if (!analysisId || !active) return

    let timer: any = null

    const scheduleReload = () => {
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
      timer = setTimeout(() => {
        load().catch(() => {
          // erros são tratados dentro de load
        })
      }, 500)
    }

    const channel = supabase
      .channel(`community_analysis_thread:${analysisId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'analysis_community_message_reactions' },
        () => scheduleReload(),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'analysis_community_reactions',
          filter: `analysis_id=eq.${analysisId}`,
        } as any,
        () => scheduleReload(),
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'analysis_community_messages',
          filter: `analysis_id=eq.${analysisId}`,
        } as any,
        () => scheduleReload(),
      )
      .subscribe()

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // ignore
      }
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
    }
  }, [active, load, analysisId])

  // Polling adicional para garantir que novas mensagens e reaÇõÇæes
  // apareÇõam mesmo se o canal realtime falhar.
  React.useEffect(() => {
    if (!analysisId || !active) return

    const interval = setInterval(() => {
      load().catch(() => {
        // silencioso; prÇüximo ciclo corrige
      })
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [active, load, analysisId])

  const sendMessage = React.useCallback(
    async (content: string, attachments?: File[]) => {
      if (!analysisId || !currentUser || !active) return

      const prepared = prepareCommunityMessageContent(String(content || ''))
      const html = prepared.html
      const files = (Array.isArray(attachments) ? attachments : [])
        .filter((file): file is File => !!file)
        .filter((file) => validateCommunityAttachmentFile(file).ok)

      if (!prepared.text && !files.length) return

      setSending(true)
      try {
        const messageId = await postAnalysisCommunityMessage(analysisId, currentUser, html)
        if (messageId) {
          emitCommunityThreadNotificationsAutoEnabled({
            contextType: 'analysis',
            contextId: analysisId,
          })
        }
        if (messageId && files.length) {
          const uploads = await Promise.all(
            files.map(async (file) => {
              const { url, bucket, path } = await uploadPublicFile(
                file,
                `analysis-community/messages/${messageId}`,
              )
              return {
                url,
                storageBucket: bucket,
                storagePath: path,
                contentType: file.type || null,
                sizeBytes: file.size,
              }
            }),
          )
          await addAnalysisMessageAttachments(messageId, uploads)
        }
        await load()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('send analysis community message error', e)
      } finally {
        setSending(false)
      }
    },
    [active, analysisId, currentUser, load],
  )

  const toggleMessageEmoji = React.useCallback(
    async (messageId: number, emoji: string) => {
      if (!analysisId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      const id = Number(messageId)
      if (!Number.isFinite(id) || id <= 0) return

      setThread((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          Number(msg.id) === id
            ? {
                ...msg,
                reactions: applyOptimisticReactionToggle(msg.reactions, trimmed),
              }
            : msg,
        ),
      }))

      void toggleAnalysisMessageReaction(id, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('useAnalysisCommunityThread toggleMessageEmoji error', error)
        load().catch(() => {
          // erro já é tratado dentro de load
        })
      })
    },
    [active, analysisId, load],
  )

  const toggleCardEmoji = React.useCallback(
    async (emoji: string) => {
      if (!analysisId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      setThread((prev) => ({
        ...prev,
        cardReactions: applyOptimisticReactionToggle(prev.cardReactions, trimmed),
      }))

      void toggleAnalysisCardReaction(analysisId, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('useAnalysisCommunityThread toggleCardEmoji error', error)
        load().catch(() => {
          // erro já é tratado dentro de load
        })
      })
    },
    [active, analysisId, load],
  )

  const deleteMessage = React.useCallback(
    async (messageId: number) => {
      if (!analysisId || !active) return
      const id = Number(messageId)
      if (!Number.isFinite(id) || id <= 0) return

      setThread((prev) => ({
        ...prev,
        messages: prev.messages.filter((msg) => Number(msg.id) !== id),
      }))

      try {
        await deleteAnalysisCommunityMessage(id)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('useAnalysisCommunityThread deleteMessage error', error)
        load().catch(() => {
          // erro já tratado dentro de load
        })
      }
    },
    [active, analysisId, load],
  )

  const editMessage = React.useCallback(
    async (messageId: number, content: string) => {
      if (!analysisId || !active) return

      const prepared = prepareCommunityMessageContent(String(content || ''))
      if (!prepared.text) return

      const baseHtml = stripEditedSuffixFromHtml(prepared.html).html
      const finalHtml = appendEditedSuffixToHtml(baseHtml)
      if (!finalHtml) return

      try {
        const updated = await updateAnalysisCommunityMessage(messageId, finalHtml)
        if (!updated) {
          await load()
          return
        }

        setThread((prev) => ({
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.id === messageId ? { ...msg, content: updated } : msg,
          ),
        }))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('useAnalysisCommunityThread editMessage error', error)
        await load()
      }
    },
    [active, analysisId, load],
  )

  return {
    thread,
    loading,
    loadedOnce,
    sending,
    reload: load,
    sendMessage,
    toggleMessageEmoji,
    toggleCardEmoji,
    deleteMessage,
    editMessage,
  }
}
